import React, { createContext, useContext, useState } from 'react';
import { generateUserJwt } from '../services/api';

const JwtContext = createContext();

export const useJwt = () => {
  const context = useContext(JwtContext);
  if (!context) {
    throw new Error('useJwt must be used within a JwtProvider');
  }
  return context;
};

export const JwtProvider = ({ children }) => {
  // JWT generation dialog state
  const [jwtDialogOpen, setJwtDialogOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [jwt, setJwt] = useState('');
  const [jwtLoading, setJwtLoading] = useState(false);
  const [jwtError, setJwtError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Handle opening the JWT dialog
  const handleOpenJwtDialog = () => {
    setJwtDialogOpen(true);
    setUserId('');
    setJwt('');
    setJwtError('');
  };

  // Handle closing the JWT dialog
  const handleCloseJwtDialog = () => {
    setJwtDialogOpen(false);
  };

  // Handle key down in user ID input
  const handleUserIdKeyDown = (e) => {
    if (e.key === 'Enter' && !jwtLoading) {
      handleGenerateJwt();
    }
  };

  // Handle JWT generation
  const handleGenerateJwt = async () => {
    if (!userId || isNaN(userId) || parseInt(userId) <= 0) {
      setJwtError('Please enter a valid user ID');
      return;
    }

    try {
      setJwtLoading(true);
      setJwtError('');
      const response = await generateUserJwt(userId);
      console.log('JWT generation response:', response);

      if (response && response.data && response.data.token) {
        setJwt(response.data.token);
      } else {
        throw new Error('Invalid response format from JWT generation API');
      }

      setJwtLoading(false);
    } catch (error) {
      console.error('JWT generation error:', error);
      setJwtLoading(false);
      setJwtError(error.message || 'Failed to generate JWT');
    }
  };

  // Handle copying JWT to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setToastMessage('JWT copied to clipboard!');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        setToastMessage('Failed to copy to clipboard');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      });
  };

  const value = {
    jwtDialogOpen,
    setJwtDialogOpen,
    userId,
    setUserId,
    jwt,
    setJwt,
    jwtLoading,
    setJwtLoading,
    jwtError,
    setJwtError,
    showToast,
    setShowToast,
    toastMessage,
    setToastMessage,
    handleOpenJwtDialog,
    handleCloseJwtDialog,
    handleUserIdKeyDown,
    handleGenerateJwt,
    copyToClipboard
  };

  return (
    <JwtContext.Provider value={value}>
      {children}
    </JwtContext.Provider>
  );
};
